<script setup lang="ts">
import MiiaFwAlertMessageLight from './AlertMessageLight.vue'
import MiiaFwAuthModal from './AuthModal.vue'
import { activate } from '../utils/miiaFwCognito'
import { reactive, ref, useTemplateRef } from 'vue'
import { useLoadingView } from '../composables/useLoadingView'
/**
 * @module components/miiafw/auth/MiiaFwActivate
 * @description MiiaFWのアクティベーション機能
 * @vue-data {Boolean} [myModal = false] モーダル制御。trueのときモーダルを開く
 * @vue-data {String} [userName = 空白] モーダル内に表示させるユーザID
 * @vue-data {String} [verificationCode = 空白] 認証コード
 */
const submit = useTemplateRef<HTMLButtonElement>('submit')
const verificationCodeInput = useTemplateRef<HTMLButtonElement>('verificationCodeInput')
const alertMessageLight =
  useTemplateRef<InstanceType<typeof MiiaFwAlertMessageLight>>('alertMessageLight')

const { setLoadingView } = useLoadingView()

const emit = defineEmits<{ 'close-top': [] }>()

const myModal = ref(false)
const userName = ref('')
const verificationCode = ref('')
const alertView = reactive<{ [key: string]: { hasError: boolean; alertMessage: string } }>({
  vc: { hasError: false, alertMessage: '' }
})

/** エラーメッセージを初期化 */
const resetAlertView = () => {
  Object.keys(alertView).forEach(key => {
    alertView[key].hasError = false
    alertView[key].alertMessage = ''
  })
}

/**
 * ユーザIDが入った状態のモーダルを開く
 * @param argUserName ユーザIDが定義済みのユーザIDならばモーダルに表示する引数
 */
const openModal = (argUserName: string): void => {
  if (argUserName !== undefined && argUserName.length > 0) {
    userName.value = argUserName
  }
  myModal.value = true
}

/**
 * モーダル内のユーザID、認証コード、エラー表示を消去した後、モーダルを閉じる
 */
const closeMyModal = (): void => {
  userName.value = ''
  verificationCode.value = ''
  resetAlertView()
  myModal.value = false
}

/**
 * 一番上のモーダルを閉じる。＜MiiaFwModalBase＞タグを指定することで、特定の操作に対応付けることができる
 */
const closeModal = (): void => {
  closeMyModal()
  emit('close-top')
}

/**
 * モーダル上でTab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e キーボードイベント
 */
const firstTab = (e: KeyboardEvent): void => {
  e.preventDefault()
  e.stopPropagation()
  submit.value?.focus()
}

/**
 * モーダル上でShift+Tab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e キーボードイベント
 */
const lastTab = (e: KeyboardEvent): void => {
  // Shift + Tabは、Tabの遡りなのでデフォルトのまま
  if (!e.shiftKey) {
    e.preventDefault()
    e.stopPropagation()
    verificationCodeInput.value?.focus()
  }
}
/**
 * 認証コードのエラー表示機能。表示されているエラーを初期化し、各項目に対してバリデーションチェックを行う。問題が無かったらmodule:modules/miiafw/MiiaFwCognito#activateへユーザIDと認証コードを渡し、モーダルを閉じる
 */
const doSubmit = (): void => {
  // エラー表示初期化
  resetAlertView()
  alertMessageLight.value?.deleteAlertMessage()
  // バリデーションチェック
  if (verificationCode.value.length < 1) {
    alertView.vc.hasError = true
    alertView.vc.alertMessage = 'Verification Codeは必須項目です'
  } else {
    // 処理中表示
    setLoadingView(true)
    // アクティベーション処理
    activate(userName.value, verificationCode.value)
      .then(() => {
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
        closeModal()
      })
      .catch(error => {
        alertView.vc.hasError = true
        alertMessageLight.value?.addAlertMessage(error.message)
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
      })
  }
}

defineExpose({
  // data
  myModal,
  userName,
  verificationCode,
  alertView,

  // methods
  openModal,
  closeMyModal,
  closeModal,
  firstTab,
  lastTab,
  doSubmit
})
</script>

<template>
  <!-- @closeを指定すると枠外クリック時にモーダルウィンドウを閉じる。 -->
  <!-- <MyModal @close="closeModal" v-if="modal"> -->
  <MiiaFwAuthModal v-if="myModal">
    <!-- default スロットコンテンツ -->
    <h4 style="font-size: 1.1rem; font-weight: 500">アクティベーション</h4>
    <div style="padding: 30px 10px 30px 10px; margin-bottom: 10px; border: 1px solid #bcbcbc">
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.vc.hasError }"
      >
        <label class="control-label">
          検証コード
          <span v-if="alertView.vc.alertMessage.length > 0" class="ff-alert-msg">
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.vc.alertMessage }}
          </span>
        </label>
        <input
          ref="verificationCodeInput"
          v-model="verificationCode"
          class="form-control ff-frm-ctrl"
          tabindex="1"
          v-focus
          @keydown.shift.tab="firstTab"
        />
      </div>
      <div style="text-align: right">
        <button
          ref="submit"
          class="btn ff-btn-primary"
          tabindex="2"
          @click="doSubmit"
          @keydown.tab="lastTab"
        >
          <font-awesome-icon icon="envelope" />
          &nbsp;検証コード送信
        </button>
      </div>
    </div>
    <MiiaFwAlertMessageLight ref="alertMessageLight"></MiiaFwAlertMessageLight>
    <!-- /default -->
    <!-- footer スロットコンテンツ -->
    <template #footer> </template>
  </MiiaFwAuthModal>
</template>

<style lang="scss" scoped>
:deep(#miiafwauthmodal-close) {
  visibility: hidden;
}
</style>
